import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRight, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import { useLanguage } from '../i18n';
import { prepareEnquiry, focusEnquiry } from '../lib/enquiry';
import { ActionLink, MOTION_EASE } from './Common';

const imageVariants = {
  enter: direction => ({ opacity: 0, x: direction > 0 ? 34 : -34, scale: .985 }),
  center: { opacity: 1, x: 0, scale: 1 },
  exit: direction => ({ opacity: 0, x: direction > 0 ? -34 : 34, scale: .985 }),
};

const detailVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: .42, ease: MOTION_EASE, staggerChildren: .055 } },
};

const detailItemVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: .34, ease: MOTION_EASE } },
};

export const ProductDialog = ({ product: p, variantIndex = 0, onVariantChange, onClose }) => {
  const { t, lang } = useLanguage();
  const reduced = useReducedMotion();
  const [photo, setPhoto] = useState(0);
  const [direction, setDirection] = useState(1);
  const enquiryRequested = useRef(false);
  const variant = p?.variants?.[variantIndex] || p?.variants?.[0];
  const total = p?.images.length || 0;

  useEffect(() => {
    setPhoto(0);
    setDirection(1);
  }, [p?.id]);

  useEffect(() => {
    if (!p || total < 2) return undefined;
    const onKey = event => {
      if (event.key === 'ArrowRight') {
        setDirection(1);
        setPhoto(current => (current + 1) % total);
      }
      if (event.key === 'ArrowLeft') {
        setDirection(-1);
        setPhoto(current => (current - 1 + total) % total);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [p, total]);

  if (!p || !variant) return <Dialog open={false}/>;

  const step = nextDirection => {
    setDirection(nextDirection);
    setPhoto(current => (current + nextDirection + total) % total);
  };
  const setThumbnail = index => {
    setDirection(index >= photo ? 1 : -1);
    setPhoto(index);
  };
  const detailTransition = reduced ? { duration: 0 } : undefined;

  return <Dialog open={!!p} onOpenChange={open => { if (!open) onClose(); }}>
    <DialogContent className="product-dialog" data-testid="product-dialog" closeLabel={t.close} data-lenis-prevent onCloseAutoFocus={event => {
      if (enquiryRequested.current) {
        event.preventDefault();
        enquiryRequested.current = false;
        focusEnquiry();
      }
    }}>
      <div className="product-dialog-grid">
        <motion.div className="product-gallery" variants={detailVariants} initial="hidden" animate="visible" transition={detailTransition}>
          <motion.div className="gallery-main" variants={detailItemVariants}>
            <span className="gallery-brand">{p.brand} / {t[p.category]}</span>
            <AnimatePresence initial={false} mode="wait" custom={direction}>
              <motion.img
                key={`${p.id}-${photo}`}
                data-testid="product-main-image"
                src={p.images[photo] || p.images[0]}
                alt={`${p.name} ${variant.kv} KV · ${t.image} ${photo + 1}`}
                custom={direction}
                variants={imageVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={reduced ? { duration: 0 } : { duration: .38, ease: MOTION_EASE }}
              />
            </AnimatePresence>
            {total > 1 && <>
              <motion.button className="gallery-nav gallery-nav-prev" aria-label={t.prevImage} onClick={() => step(-1)} variants={detailItemVariants} whileHover={reduced ? undefined : { x: -3 }} whileTap={reduced ? undefined : { scale: .94 }}><ChevronLeft size={18}/></motion.button>
              <motion.button className="gallery-nav gallery-nav-next" aria-label={t.nextImage} onClick={() => step(1)} variants={detailItemVariants} whileHover={reduced ? undefined : { x: 3 }} whileTap={reduced ? undefined : { scale: .94 }}><ChevronRight size={18}/></motion.button>
            </>}
            <span className="gallery-pagination">{String(photo + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
          </motion.div>
          {total > 1 && <motion.div className="gallery-thumbnails" variants={detailItemVariants}>{p.images.map((src, i) => <motion.button key={src} aria-label={`${t.image} ${i + 1}`} aria-pressed={photo === i} onClick={() => setThumbnail(i)} className={photo === i ? 'active' : ''} whileHover={reduced ? undefined : { y: -3 }} whileTap={reduced ? undefined : { scale: .96 }}><img src={src} alt=""/></motion.button>)}</motion.div>}
          {p.note === 'photoPending' && <motion.p className="photo-note" variants={detailItemVariants}>{t.photoPending}</motion.p>}
        </motion.div>

        <motion.div className="product-dialog-info" variants={detailVariants} initial="hidden" animate="visible" transition={detailTransition}>
          <motion.span className="dialog-kv" variants={detailItemVariants}>{variant.kv} KV</motion.span>
          <DialogTitle className="product-dialog-title">{p.name}</DialogTitle>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div key={`${p.id}-${variantIndex}`} className="product-dialog-data" variants={detailVariants} initial="hidden" animate="visible" exit="hidden">
              <motion.div variants={detailItemVariants}><DialogDescription className="product-dialog-description">{variant.descriptions?.[lang] || p.description?.[lang]}</DialogDescription></motion.div>
              <motion.div variants={detailItemVariants} className="dialog-variants" role="tablist" aria-label={t.variantLabel}>{p.variants.map((v, i) => <motion.button key={v.kv} role="tab" aria-selected={variantIndex === i} className={variantIndex === i ? 'active' : ''} onClick={() => onVariantChange(i)} whileHover={reduced ? undefined : { y: -2 }} whileTap={reduced ? undefined : { scale: .96 }}>{v.kv} KV</motion.button>)}</motion.div>
              <motion.div className="dialog-price" variants={detailItemVariants}><strong>{variant.price}</strong><span>{t.volumePricing}</span>{p.note === 'priceDepends' && <small>{t.priceDepends}</small>}</motion.div>
              <motion.div variants={detailItemVariants}><h3 className="spec-table-heading">{t.specifications}</h3><dl className="spec-table">{Object.entries(variant.specs).map(([key, value]) => <div key={key}><dt>{t.spec?.[key] || key.replaceAll('_', ' ')}</dt><dd>{value}</dd></div>)}</dl></motion.div>
              {p.note === 'cells3115' && <motion.div className="spec-warning" variants={detailItemVariants}><Info size={16}/><p>{t.note3115}</p></motion.div>}
              <motion.p className="source-note" variants={detailItemVariants}>{t.sourceNote}</motion.p>
            </motion.div>
          </AnimatePresence>
          <motion.div className="product-inquiry-wrap" variants={detailItemVariants}><ActionLink className="product-inquiry-link" testId="product-inquiry" href="#contact" onClick={event => {
            event.preventDefault();
            prepareEnquiry(`${t.subject} · ${p.name} ${variant.kv} KV`);
            enquiryRequested.current = true;
            onClose();
          }}>{t.askModel}</ActionLink></motion.div>
          <motion.a className="source-link" href="#contact" onClick={onClose} variants={detailItemVariants}>{t.contactLink}<ArrowUpRight size={12}/></motion.a>
        </motion.div>
      </div>
    </DialogContent>
  </Dialog>;
};
